import _ from 'lodash';
import type {
  ChatContextGitHubRepository,
  ChatContextGitHubRepositoryFile,
  ChatContextItem,
} from './types';

/**
 * Format the context for the API.
 *
 * @param context - The context to format.
 * @returns The formatted context.
 */
export function formatContextForAPI(context: ChatContextItem[]) {
  return {
    githubRepositories: getChatContextGitHubRepositories(context),
    githubRepositoryFiles: getChatContextGitHubRepositoryFiles(context).map(
      (file) => ({
        ...file,
        githubRepository: {
          ...file.githubRepository,
          id: file.githubRepository.id.toString(),
          owner: {
            ...file.githubRepository.owner,
            id: file.githubRepository.owner.id.toString(),
          },
        },
      })
    ),
  };
}

/**
 * Check if the item is a GitHub repository.
 *
 * @param item - The item to check.
 * @returns True if the item is a GitHub repository, false otherwise.
 */
export function isChatContextGitHubRepository(
  item: ChatContextItem
): item is ChatContextGitHubRepository {
  return _.has(item, 'owner');
}

/**
 * Check if the item is a GitHub repository file.
 *
 * @param item - The item to check.
 * @returns True if the item is a GitHub repository file, false otherwise.
 */
export function isChatContextGitHubRepositoryFile(
  item: ChatContextItem
): item is ChatContextGitHubRepositoryFile {
  return _.has(item, 'path');
}

/**
 * Get the GitHub repositories from the context.
 *
 * @param items - The items to get the GitHub repositories from.
 * @returns The GitHub repositories from the context.
 */
export function getChatContextGitHubRepositories(
  items: ChatContextItem[]
): ChatContextGitHubRepository[] {
  return items.filter(isChatContextGitHubRepository);
}

/**
 * Get the GitHub repository files from the context.
 *
 * @param items - The items to get the GitHub repository files from.
 * @returns The GitHub repository files from the context.
 */
export function getChatContextGitHubRepositoryFiles(
  items: ChatContextItem[]
): ChatContextGitHubRepositoryFile[] {
  return items.filter(isChatContextGitHubRepositoryFile);
}
